import { useNavigate } from 'react-router-dom'
import { Lock, RefreshCw, LogOut } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuthStore } from '../../store/authStore'
import { useTenantAccess } from '../../hooks/useTenantAccess'
import { useCurrency } from '../../hooks/useCurrency'
import { logAuthEvent } from '../../lib/auditLog'

// Full-page wall shown by AppLayout when the tenant's subscription has lapsed
// (see subscriptionLock.isTenantLockedOut). No sidebar, no working pages behind it.
// Role-aware: owner/admin get a Renew button → /subscription; anyone else is told to
// ask their administrator. Logout is always available so the choke point is escapable.
export function SubscriptionLockScreen() {
  const navigate = useNavigate()
  const { tenant, role, clearSession } = useAuthStore()
  const { plan } = useTenantAccess()
  const { format } = useCurrency()

  const canPay = role === 'owner' || role === 'admin'

  async function handleLogout() {
    const { id: tid } = tenant ?? {}
    const uid = useAuthStore.getState().user?.id
    await logAuthEvent(tid, uid, 'logout', 'Signed out')
    try { await supabase.auth.signOut() } catch { /* clear locally regardless */ }
    clearSession()
    navigate('/')
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="w-full max-w-md card p-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50">
          <Lock className="h-7 w-7 text-brand-500" />
        </div>

        {canPay ? (
          <>
            <h1 className="text-xl font-semibold text-gray-900">Hello there — a subscription is required to continue</h1>
            <p className="mt-2 text-sm text-gray-500">
              {tenant?.name ? `${tenant.name}'s ` : 'Your '}subscription has lapsed. Renew to get everyone back to work.
            </p>
            {plan && (
              <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-sm font-medium text-gray-900">{plan.name}</p>
                <p className="text-xs text-gray-500">
                  {typeof plan.monthly_price === 'number' ? `${format(plan.monthly_price)} / month` : ''}
                </p>
              </div>
            )}
            <button className="btn-primary w-full mt-5" onClick={() => navigate('/subscription')}>
              <RefreshCw className="h-4 w-4" /> Renew / Pay
            </button>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-gray-900">Subscription lapsed</h1>
            <p className="mt-2 text-sm text-gray-500">
              This account&apos;s subscription has lapsed — please ask your administrator to renew.
            </p>
          </>
        )}

        <button className="btn-secondary w-full mt-3" onClick={handleLogout}>
          <LogOut className="h-4 w-4" /> Log out
        </button>
      </div>
    </div>
  )
}
